import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
    Box, Grid, Card, CardContent, Typography, CircularProgress,
    Chip, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Paper, LinearProgress, TablePagination, Tooltip, IconButton
} from '@mui/material';
import {
    FolderSpecial, CheckCircle, Warning, EmojiEvents, FilterList
} from '@mui/icons-material';
import { getFacilityProjectDashboard } from '../../../Redux/actions';
import Filter from '../../Common/Filter';
import { updateParams } from '../../../utils';
import moment from 'moment';

/* ── Gantt helpers ─────────────────────────────────────────────── */
// Show a rolling 5-month window anchored to the current month
const buildGanttMonths = () => {
    const months = [];
    const start = moment().subtract(1, 'month');
    for (let i = 0; i < 5; i++) {
        months.push(moment(start).add(i, 'month'));
    }
    return months;
};
const GANTT_MONTHS = buildGanttMonths();
const GANTT_START = GANTT_MONTHS[0].clone().startOf('month');
const GANTT_END   = GANTT_MONTHS[GANTT_MONTHS.length - 1].clone().endOf('month');
const GANTT_TOTAL_MS = GANTT_END.valueOf() - GANTT_START.valueOf();

const BAR_COLORS = ['#f48fb1', '#ffb74d', '#64b5f6', '#81c784', '#ba68c8', '#4db6ac'];

const GanttBar = ({ subTasks = [], colorIndex = 0 }) => {
    if (!subTasks || subTasks.length === 0) return <Box sx={{ color: '#ccc', fontSize: '0.75rem', pl: 1 }}>—</Box>;

    const baseColor = BAR_COLORS[colorIndex % BAR_COLORS.length];

    return (
        <Box sx={{ position: 'relative', width: '100%', display: 'flex', flexDirection: 'column', gap: '6px', py: 1 }}>
            {subTasks.map((st, i) => {
                if (!st.start_date && !st.end_date) return null;

                const s = st.start_date ? moment(st.start_date) : GANTT_START.clone();
                const e = st.end_date   ? moment(st.end_date)   : GANTT_END.clone();

                const clampedStart = moment.max(s, GANTT_START).valueOf();
                const clampedEnd   = moment.min(e, GANTT_END).valueOf();
                
                if (clampedEnd < clampedStart) return null;

                const leftPct  = ((clampedStart - GANTT_START.valueOf()) / GANTT_TOTAL_MS) * 100;
                const widthPct = ((clampedEnd   - clampedStart)          / GANTT_TOTAL_MS) * 100;
                
                // Adjust opacity slightly for distinct sub-bars if there are many, or just use baseColor
                const tooltipText = `${st.description || 'Sub-task'}: ${st.start_date ? moment(st.start_date).format('DD MMM') : 'TBD'} - ${st.end_date ? moment(st.end_date).format('DD MMM') : 'TBD'} (${st.status || 'Active'})`;

                return (
                    <Box key={st.id || i} sx={{ position: 'relative', width: '100%', height: 16 }}>
                        <Tooltip title={tooltipText} placement="top" arrow>
                            <Box sx={{
                                position: 'absolute',
                                left: `${leftPct}%`,
                                width: `${Math.max(widthPct, 1.5)}%`,
                                height: 16,
                                bgcolor: baseColor,
                                borderRadius: '4px',
                                opacity: st.status === 'Completed' ? 0.6 : 0.9,
                                cursor: 'pointer',
                                transition: 'opacity 0.2s, filter 0.2s',
                                '&:hover': { opacity: 1, filter: 'brightness(1.1)' }
                            }} />
                        </Tooltip>
                    </Box>
                );
            })}
        </Box>
    );
};

/* ─────────────────────────── helpers ─────────────────────────── */
const formatCurrencyShort = (value) => {
    if (!value || isNaN(value)) return '₹0';
    if (value >= 10000000) return `₹${(value / 10000000).toFixed(1)}Cr`;
    if (value >= 100000) return `₹${(value / 100000).toFixed(1)}L`;
    if (value >= 1000) return `₹${(value / 1000).toFixed(1)}K`;
    return `₹${Number(value).toFixed(0)}`;
};

const getStatusStyle = (status) => {
    switch (status) {
        case 'Completed':
            return { color: '#196a3e', bgcolor: '#f1faf4', border: '1px solid #c2e9cf' };
        case 'Active':
            return { color: '#1a6894', bgcolor: '#f1f8fc', border: '1px solid #b8ddec' };
        case 'Delayed':
            return { color: '#c62828', bgcolor: '#ffebee', border: '1px solid #ffcdd2' };
        case 'On Hold':
            return { color: '#8f6d1d', bgcolor: '#fffdee', border: '1px solid #eed699' };
        default:
            return { color: '#757575', bgcolor: '#f5f5f5', border: '1px solid #e0e0e0' };
    }
};

const getOwnerStyle = (name) => {
    if (!name) return { color: '#757575', bgcolor: '#f5f5f5' };
    const hash = Array.from(name).reduce((acc, char) => acc + char.charCodeAt(0), 0) % 4;
    return [
        { color: '#196a3e', bgcolor: '#f1faf4' },
        { color: '#1a6894', bgcolor: '#f1f8fc' },
        { color: '#a44d21', bgcolor: '#fff6ef' },
        { color: '#880e4f', bgcolor: '#fce4ec' },
    ][hash];
};

/* ─────────────────────────── KPI card ─────────────────────────── */
const KPICard = ({ title, value, subtitle, icon, gradient, bordered }) => {
    if (bordered) {
        return (
            <Card sx={{
                height: '100%',
                border: '1px solid #eaeaea',
                borderLeft: `4px solid ${bordered}`,
                borderRadius: 2,
                boxShadow: 'none',
            }}>
                <CardContent sx={{ p: 2.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#888' }}>
                        {title}
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, my: 0.5, color: bordered }}>
                        {value}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#888', fontWeight: 500 }}>
                        {subtitle}
                    </Typography>
                </CardContent>
            </Card>
        );
    }
    return (
        <Card sx={{
            height: '100%',
            background: gradient,
            color: '#fff',
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            position: 'relative',
            overflow: 'hidden',
        }}>
            <Box sx={{ position: 'absolute', right: -20, top: -20, opacity: 0.15 }}>
                {icon && React.cloneElement(icon, { sx: { fontSize: 140 } })}
            </Box>
            <CardContent sx={{ position: 'relative', zIndex: 1, p: 3 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, mb: 1, opacity: 0.9 }}>
                    {title}
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {value}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500, opacity: 0.85 }}>
                    {subtitle}
                </Typography>
            </CardContent>
        </Card>
    );
};

/* ─────────────────────────── main component ─────────────────────────── */
export default function ProjectManagement() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [queryParams] = useSearchParams();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [showFilterDialog, setShowFilterDialog] = useState(false);
    const [isSearchOrFilterApplied, setIsSearchOrFilterApplied] = useState(false);
    const [pmFilterOptions, setPmFilterOptions] = useState({});

    useEffect(() => {
        setPage(0);
        fetchData();
    }, [queryParams]);

    const fetchData = () => {
        setLoading(true);
        const existingParams = Object.fromEntries([...queryParams]);
        const params = {
            location: existingParams?.location || '',
            facility: existingParams?.facility || '',
            projectOwner: existingParams?.projectOwner || '',
        };
        dispatch(getFacilityProjectDashboard(params)).then((res) => {
            if (res?.data?.success) {
                setData(res.data.data);
                if (res.data.data?.filterOptions) {
                    setPmFilterOptions(res.data.data.filterOptions);
                }
            }
            setLoading(false);
        }).catch(() => setLoading(false));
    };

    const updateQueryParams = (params) => {
        const existingParams = Object.fromEntries([...queryParams]);
        const searchParams = updateParams(existingParams, params);
        navigate({ search: searchParams.toString() }, { replace: true });
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress sx={{ color: '#008080' }} />
            </Box>
        );
    }

    if (!data) return null;

    const { summary, projects, spocRanking } = data;

    const displayedProjects = projects.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
        <Box sx={{ p: 2 }}>
            {/* Filter Button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                <IconButton onClick={() => setShowFilterDialog(true)} title="Apply Filter">
                    <FilterList />
                </IconButton>
            </Box>

            <Filter
                filterArea="ProjectManagement"
                showFilterDialog={showFilterDialog}
                setShowFilterDialog={setShowFilterDialog}
                updateQueryParams={updateQueryParams}
                queryParams={queryParams}
                setIsSearchOrFilterApplied={setIsSearchOrFilterApplied}
                pmFilterOptions={pmFilterOptions}
            />

            {/* ── KPI Summary Cards ─────────────────────────── */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <KPICard
                        title="Active Projects"
                        value={summary.activeProjects}
                        subtitle={`${summary.totalFacilities || 0} ${summary.totalFacilities === 1 ? 'city' : 'cities'}`}
                        icon={<FolderSpecial />}
                        gradient="linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <KPICard
                        title="On Schedule"
                        value={summary.onSchedule}
                        subtitle={`${summary.activeProjects > 0 ? Math.round((summary.onSchedule / summary.activeProjects) * 100) : 0}% on track`}
                        icon={<CheckCircle />}
                        gradient="linear-gradient(135deg, #11998e 0%, #38ef7d 100%)"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <KPICard
                        title="Delayed"
                        value={summary.delayed}
                        subtitle="needs attention"
                        bordered="#c62828"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <KPICard
                        title="Completed"
                        value={summary.completed}
                        subtitle={`${formatCurrencyShort(summary.totalBudget)} total budget`}
                        icon={<EmojiEvents />}
                        gradient="linear-gradient(135deg, #f2994a 0%, #f2c94c 100%)"
                    />
                </Grid>
            </Grid>

            {/* ── Bottom Section ─────────────────────────── */}
            <Grid container spacing={4}>

                {/* ── Project Timeline Table ─────────────────────── */}
                <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#555', letterSpacing: 0.5, textTransform: 'uppercase' }}>
                        All Projects — Timeline &amp; Status
                    </Typography>
                    <Paper sx={{ borderRadius: 2, boxShadow: 'none', border: '1px solid #eaeaea' }}>
                        <TableContainer sx={{ maxHeight: 600 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        {['Project', 'Location', 'SPOC', 'Budget', 'Progress'].map((col) => (
                                            <TableCell key={col} sx={{ fontWeight: 600, color: '#666', fontSize: '0.75rem', letterSpacing: 0.5, bgcolor: '#fafafa', py: 1.5, whiteSpace: 'nowrap', position: 'sticky', top: 0, zIndex: 10 }}>
                                                {col}
                                            </TableCell>
                                        ))}
                                        <TableCell sx={{ bgcolor: '#fafafa', p: 0, minWidth: 300, position: 'sticky', top: 0, zIndex: 10 }}>
                                            <Box sx={{ display: 'flex', width: '100%' }}>
                                                {GANTT_MONTHS.map((m) => (
                                                    <Box key={m.format('YYYY-MM')} sx={{ flex: 1, textAlign: 'center', py: 1.5, fontWeight: 700, color: '#666', fontSize: '0.72rem', letterSpacing: 0.5, borderLeft: '1px dashed #eaeaea' }}>
                                                        {m.format('MMM').toUpperCase()}
                                                    </Box>
                                                ))}
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 600, color: '#666', fontSize: '0.75rem', letterSpacing: 0.5, bgcolor: '#fafafa', py: 1.5, position: 'sticky', top: 0, zIndex: 10 }}>Status</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {displayedProjects.map((proj, idx) => {
                                        const owners = Array.isArray(proj.project_owner) ? proj.project_owner : [];
                                        const ownerName = owners.length > 0
                                            ? (typeof owners[0] === 'object' ? (owners[0].name || owners[0].label || 'N/A') : owners[0])
                                            : 'Unassigned';
                                        const ownerStyle = getOwnerStyle(ownerName);
                                        const statusStyle = getStatusStyle(proj.status);
                                        const progressColor = proj.status === 'Delayed' ? '#c62828' : proj.status === 'Completed' ? '#196a3e' : '#008080';

                                        return (
                                            <TableRow key={proj.id || idx} sx={{ '& td': { borderBottom: '1px solid #f0f0f0', py: 1.5 } }}>
                                                <TableCell sx={{ maxWidth: '250px' }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#222', wordBreak: 'break-word', whiteSpace: 'normal' }}>
                                                        {proj.facility_name}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: '#666', wordBreak: 'break-word', whiteSpace: 'normal', display: 'block', mt: 0.5 }}>
                                                        {proj.description || `Project #${proj.id}`}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ color: '#555', fontSize: '0.85rem' }}>
                                                        {proj.location?.name || '-'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                        {owners.length > 0 ? owners.map((owner, ownerIdx) => {
                                                            const name = typeof owner === 'object' ? (owner.name || owner.label || 'N/A') : owner;
                                                            const style = getOwnerStyle(name);
                                                            return (
                                                                <Box key={ownerIdx} sx={{ display: 'inline-flex', alignItems: 'center', ...style, px: 1.2, py: 0.3, borderRadius: 4, width: 'fit-content' }}>
                                                                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: style.color, mr: 0.8 }} />
                                                                    <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                                                                        {name}
                                                                    </Typography>
                                                                </Box>
                                                            );
                                                        }) : (
                                                            <Box sx={{ display: 'inline-flex', alignItems: 'center', ...ownerStyle, px: 1.2, py: 0.3, borderRadius: 4, width: 'fit-content' }}>
                                                                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: ownerStyle.color, mr: 0.8 }} />
                                                                <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                                                                    Unassigned
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 600, color: '#333', whiteSpace: 'nowrap' }}>
                                                    {proj.project_budget ? formatCurrencyShort(proj.project_budget) : '—'}
                                                </TableCell>
                                                <TableCell sx={{ minWidth: 120 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <LinearProgress
                                                            variant="determinate"
                                                            value={proj.progress}
                                                            sx={{
                                                                flexGrow: 1,
                                                                height: 6,
                                                                borderRadius: 3,
                                                                bgcolor: '#f0f0f0',
                                                                '& .MuiLinearProgress-bar': { bgcolor: progressColor, borderRadius: 3 }
                                                            }}
                                                        />
                                                        <Typography variant="caption" sx={{ fontWeight: 600, color: progressColor, minWidth: 30 }}>
                                                            {proj.progress}%
                                                        </Typography>
                                                    </Box>
                                                    <Typography variant="caption" sx={{ color: '#999' }}>
                                                        {proj.completed_sub_tasks}/{proj.total_sub_tasks} sub-tasks
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ p: 0, minWidth: 280, verticalAlign: 'top' }}>
                                                    <GanttBar
                                                        subTasks={proj.sub_tasks}
                                                        colorIndex={idx}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={proj.status}
                                                        size="small"
                                                        sx={{ fontWeight: 600, minWidth: 75, ...statusStyle, border: 'none' }}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                    {projects.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center" sx={{ py: 4, color: '#888' }}>
                                                No projects found.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        {projects.length > 0 && (
                            <TablePagination
                                rowsPerPageOptions={[10, 25, 50]}
                                component="div"
                                count={projects.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={(e, newPage) => setPage(newPage)}
                                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                                labelRowsPerPage="Rows:"
                                sx={{
                                    borderTop: '1px solid #eaeaea',
                                    '.MuiTablePagination-spacer': { display: 'none' },
                                    '.MuiInputBase-root': { marginRight: 'auto' },
                                    '.MuiSelect-select': { minWidth: '15px !important', width: '15px' },
                                    '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                                        marginTop: '0px !important', marginBottom: '0px !important'
                                    }
                                }}
                            />
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
}
